// svgFurigana.js — reading aid (ルビ) for D3/SVG chart text.
//
// HTML <ruby> is INVALID inside <svg>, so the composite FuriganaText component
// cannot annotate chart labels (it deliberately skips <svg>). This module is the
// SVG-native counterpart: a dependency-free POST-RENDER pass that scans an <svg>'s
// <text> nodes and, for each kanji-bearing label, inserts a sibling half-size
// reading <text> positioned just above it.
//
// Design choices (and why):
//  - LABEL-LEVEL reading line, not per-character ruby. Precise per-kanji x-centering
//    in SVG needs live text measurement (getComputedTextLength/getSubStringLength),
//    which is unavailable at build time and untestable in jsdom. Chart labels are
//    short (axis titles, legend entries, tick categories), so one reading line above
//    the label — sharing the base's x / text-anchor / transform — aligns well and is
//    fully deterministic. Kana inside a mixed label is preserved, so the line reads
//    naturally (割合と傾向 → わりあいとけいこう).
//  - The reading node is aria-hidden: screen readers already read the kanji base
//    correctly, so the visual reading must NOT be double-announced.
//  - pointer-events:none + user-select:none: the reading never intercepts clicks and
//    never pollutes a text selection (chart labels aren't selection-driven, but this
//    keeps the base string authoritative, mirroring the HTML <rt> rule).
//  - Idempotent + re-render safe: the base is stamped data-furigana; the reading is
//    stamped data-furigana-rt. A full InteractiveGraph redraw clears the svg, but the
//    engine arriving late or the toggle flipping does NOT redraw: refreshSvgFurigana
//    strips the previous pass and re-runs it on the chart that is already drawn.
import { annotateFurigana, isKanjiChar } from './furigana';

const SVGNS = 'http://www.w3.org/2000/svg';
const RT_SCALE = 0.5; // reading font-size relative to base
const RT_MIN = 8; // never smaller than this (px)
const RT_RISE = 0.9; // how far above the base baseline (× base font-size)

/**
 * The kana reading line for a label: each kanji segment replaced by its reading,
 * all other text (kana / latin / digits / emoji / punctuation) kept verbatim.
 * Returns null when the label has no covered kanji — nothing to render.
 * @param {string} text
 * @param {{words:object, kanji:object}} [dicts]
 * @returns {string|null}
 */
export function buildReadingLine(text, dicts) {
  if (typeof text !== 'string' || text.length === 0) return null;
  const segs = annotateFurigana(text, dicts);
  if (!segs.some((s) => s.reading)) return null;
  return segs.map((s) => (s.reading ? s.reading : s.text)).join('');
}

function baseFontSize(el) {
  const styled = el.style && el.style.fontSize;
  let size = parseFloat(styled || el.getAttribute('font-size'));
  if (!(Number.isFinite(size) && size > 0) && typeof window !== 'undefined' && window.getComputedStyle) {
    // No inline / attr size → the base INHERITS it (e.g. an axis <g> font-size of
    // 20–32px). Read the computed size so the reading scales with the real label,
    // instead of falling back to a too-small default. (jsdom has no layout → falls back.)
    try { size = parseFloat(window.getComputedStyle(el).fontSize); } catch (e) { /* no layout */ }
  }
  return Number.isFinite(size) && size > 0 ? size : 12;
}

/**
 * Add a reading <tspan> ABOVE every kanji-bearing <text> in an svg subtree. The
 * reading is a CHILD of the base <text>, so it inherits the base's transform / fill /
 * text-anchor and — critically — its display / visibility / opacity: when a renderer
 * hides, fades, moves or removes the base label (focus dimming, stage change, pie
 * animation, drag), the reading goes with it — no leaking of hidden stage content and
 * no orphan on overlay re-apply. No-op when disabled, the element is missing, or a
 * label has no covered kanji.
 * @param {SVGElement|null|undefined} svgEl the <svg> (or any subtree) to scan
 * @param {{enabled?:boolean, dicts?:object}} [opts]
 */
export function renderSvgFurigana(svgEl, opts = {}) {
  const { enabled = true, dicts } = opts;
  if (!enabled || !svgEl || typeof svgEl.querySelectorAll !== 'function') return;

  // querySelectorAll('text') never returns the reading <tspan> children, so a re-run
  // only sees base <text>; the data-furigana stamp makes it idempotent.
  const texts = Array.from(svgEl.querySelectorAll('text'));
  for (const base of texts) {
    if (base.getAttribute('data-furigana')) continue; // already processed

    const raw = base.textContent;
    if (!raw || !raw.trim() || !Array.from(raw).some(isKanjiChar)) {
      base.setAttribute('data-furigana', 'none');
      continue;
    }
    const readingLine = buildReadingLine(raw, dicts);
    if (readingLine == null) {
      base.setAttribute('data-furigana', 'none');
      continue;
    }

    const fontSize = baseFontSize(base);
    const rt = document.createElementNS(SVGNS, 'tspan');
    // Always set an absolute x so the reading sits ABOVE the base (not after it): reset
    // to the base's x, or 0 when the base relies on the default x=0 (e.g. inside a
    // translated <g>). Without this a label with no x would lay the reading beside the
    // base and text-anchor:middle could recenter and shift the original label.
    const x = base.getAttribute('x');
    rt.setAttribute('x', x != null ? x : '0');
    rt.setAttribute('dy', String(-(fontSize * RT_RISE))); // above the base baseline
    rt.setAttribute('font-size', String(Math.max(RT_MIN, Math.round(fontSize * RT_SCALE))));
    rt.setAttribute('data-furigana-rt', '1');
    rt.setAttribute('aria-hidden', 'true');
    rt.style.pointerEvents = 'none';
    rt.style.userSelect = 'none';
    rt.style.webkitUserSelect = 'none';
    rt.textContent = readingLine; // inherits the base fill / transform / visibility
    base.appendChild(rt);
    base.setAttribute('data-furigana', 'applied');
  }
}

/**
 * Undo renderSvgFurigana in place: remove every reading <tspan> and every
 * data-furigana stamp, leaving each base <text> exactly as its renderer emitted it.
 * The 'none' stamps must go too — a label processed before the engine was ready got
 * 'none' only because no reading was available yet, and would otherwise be skipped
 * for good once it is.
 * @param {SVGElement|null|undefined} svgEl
 */
export function clearSvgFurigana(svgEl) {
  if (!svgEl || typeof svgEl.querySelectorAll !== 'function') return;
  svgEl.querySelectorAll('tspan[data-furigana-rt]').forEach((rt) => rt.remove());
  svgEl.querySelectorAll('text[data-furigana]').forEach((base) => base.removeAttribute('data-furigana'));
}

/**
 * Re-run the reading pass over an svg that is already drawn: clear the previous
 * pass, then apply it again when enabled (or leave the labels bare when not). Same
 * end state as a fresh draw followed by renderSvgFurigana, without recreating nodes.
 * @param {SVGElement|null|undefined} svgEl
 * @param {{enabled?:boolean, dicts?:object}} [opts]
 */
export function refreshSvgFurigana(svgEl, opts = {}) {
  clearSvgFurigana(svgEl);
  renderSvgFurigana(svgEl, opts);
}
